/**
 * Crewlink's shared error classification — the one place that decides whether a
 * failure is an EXPECTED BUSINESS OUTCOME or an UNEXPECTED FAULT.
 *
 * **THE PROBLEM THIS EXISTS TO SOLVE (I-006, I-007 and its F5).**
 *
 * PostgREST maps every `raise exception` to HTTP 500, so a refusal that is a
 * completely normal part of using Crewlink — tapping Capture on a Closed
 * Project, saving a follow-up someone else already moved — arrives at the
 * client looking exactly like a server fault. With no monitoring configured
 * that only affected wording. With monitoring configured it would fill an
 * error dashboard with routine business noise, and real 500s would drown in
 * it. A dashboard nobody trusts is a dashboard nobody reads.
 *
 * **SUPPRESSION REQUIRES STRUCTURE. WORDING MAY FALL BACK TO TEXT.**
 *
 * That split is the heart of this module, and it is deliberate:
 *
 *   * `classifyError` — which decides whether an event is REPORTED — reads
 *     ONLY the structured `code` PostgREST returns. Message text is not a
 *     contract: rewording a `raise exception` in a future migration must
 *     never silently change what monitoring sees, and an unrelated fault
 *     whose message happens to contain "CL001" must never be swallowed.
 *   * `isStaleWriteRefusal` — which only decides WHICH SENTENCE Brandon
 *     reads — may fall back to matching the message, because being wrong
 *     there costs a less specific explanation and nothing else. The screen
 *     re-reads server truth on any failure regardless (I-006).
 *
 * **UNKNOWN IS ALWAYS REPORTABLE.** Anything this module does not positively
 * recognise as a Crewlink business rule stays `unexpected`. Silence is the
 * failure mode that costs a field bug its evidence, so it is never the
 * default.
 */

/**
 * `object_not_in_prerequisite_state`. Crewlink's writers raise it for the
 * optimistic-concurrency guard: the row changed since it was read.
 */
export const STALE_WRITE_CODE = '55000';

/** Crewlink's own business-rule codes, raised as `using errcode = 'CLnnn'`. */
const CREWLINK_BUSINESS_CODE = /^CL\d{3}$/;

export type CrewlinkErrorClassification =
  | {
      /** A rule working as designed. Handled by the product, never reported. */
      readonly kind: 'business';
      readonly code: string;
    }
  | {
      /** Anything else — including anything unrecognised. Always reportable. */
      readonly kind: 'unexpected';
      /** The technical code when there is one. Never product content. */
      readonly code: string | null;
    };

/**
 * The structured error code, if the value carries one.
 *
 * Reads `code` from a PostgREST/Supabase error object. Returns null for a bare
 * `Error`, a string, or anything else without a usable code — deliberately,
 * because "no structured code" must never be mistaken for a known one.
 */
export function errorCodeOf(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/** The raw message, when there is one. Used for WORDING only, never suppression. */
function messageOf(error: unknown): string {
  if (typeof error === 'string') return error;
  if (typeof error !== 'object' || error === null) return '';
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' ? message : '';
}

/**
 * Is this code one of Crewlink's expected business outcomes?
 *
 * Exactly two families, both raised deliberately by Crewlink's own SQL:
 * the stale-write guard, and the `CL` rule codes. Nothing else — notably NOT
 * `P0002` (`no_data_found`) and NOT `42501` (`insufficient_privilege`), which
 * Crewlink does raise on purpose in places but which a genuine defect can also
 * produce. Classifying those as handled would hide real faults, so they stay
 * reportable until that trade is separately decided.
 */
function isBusinessCode(code: string): boolean {
  return code === STALE_WRITE_CODE || CREWLINK_BUSINESS_CODE.test(code);
}

/**
 * Classify a failure. STRUCTURED CODE ONLY — see the header.
 */
export function classifyError(error: unknown): CrewlinkErrorClassification {
  const code = errorCodeOf(error);
  if (code !== null && isBusinessCode(code)) return { kind: 'business', code };
  return { kind: 'unexpected', code };
}

/** True when the failure is a rule doing its job, so nothing needs reporting. */
export function isBusinessRefusal(error: unknown): boolean {
  return classifyError(error).kind === 'business';
}

/**
 * Was this the optimistic-concurrency guard — "someone changed it first"?
 *
 * **Structured first (I-006's fix), message as a fallback.** The fallback is
 * intentionally kept: some writers surface only a message, and here a wrong
 * answer costs a less specific sentence rather than a missing error report.
 */
export function isStaleWriteRefusal(error: unknown): boolean {
  if (errorCodeOf(error) === STALE_WRITE_CODE) return true;
  const message = messageOf(error).toLowerCase();
  return message.includes('changed since it was read') || message.includes(STALE_WRITE_CODE);
}
